/* eslint-disable @typescript-eslint/no-magic-numbers */
import { HttpHeaders } from '@angular/common/http';
import { KeycloakEvent } from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { Observable, Subject, of } from 'rxjs';

import { PermissionStoreModelEnum } from '@domain/permissions/permissions.enums';

const SystemAdminPermissions = [
  PermissionStoreModelEnum.ViewAnySites,
  PermissionStoreModelEnum.ViewAnyDashboard,
  PermissionStoreModelEnum.ManageAnyChargerBasicCommands,
  PermissionStoreModelEnum.ManageAnyChargerPowerCommand,
  PermissionStoreModelEnum.ManageAnyChargerResetCommand,
  PermissionStoreModelEnum.ManageAnyChargerReconnect,
  PermissionStoreModelEnum.ManageAnyChargerCustomCommand,
  PermissionStoreModelEnum.ManageAnyChargerConfiguration,
  PermissionStoreModelEnum.ManageAnyChargerDiagnostics,
  PermissionStoreModelEnum.ViewAnyChargerConfiguration,
  PermissionStoreModelEnum.ManageAnyChargingSessionPriority,
  PermissionStoreModelEnum.ManageAnyBessCommands,
  PermissionStoreModelEnum.ViewAnyAnalytics,
  PermissionStoreModelEnum.ViewAnyFinanceAnalytics,
  PermissionStoreModelEnum.ViewAnyIdTag,
  PermissionStoreModelEnum.ManageAnyIdTag,
  PermissionStoreModelEnum.ViewAnyUsers,
  PermissionStoreModelEnum.ManageAnyUsers,
  PermissionStoreModelEnum.ImpersonateAnyUser,
  PermissionStoreModelEnum.ManageAnySchedule,
  PermissionStoreModelEnum.ViewAnyVehicles,
  PermissionStoreModelEnum.ViewWorkflow,
  PermissionStoreModelEnum.RestartWorkflowStep,
  PermissionStoreModelEnum.ResolveWorkflowStep,
  PermissionStoreModelEnum.ManageWorkflowAdminActions,
  PermissionStoreModelEnum.StartNewWorkflow,
  PermissionStoreModelEnum.ManageAnyLoadProfileTrigger,
  PermissionStoreModelEnum.ViewAnyAuditLogs,
  PermissionStoreModelEnum.ViewAnyKiosk,
  PermissionStoreModelEnum.ManageAnyAlert,
  PermissionStoreModelEnum.ViewAnyReports,
  PermissionStoreModelEnum.ViewDataflow,
  PermissionStoreModelEnum.ViewAnySiteWithAiAssistant,
  PermissionStoreModelEnum.ManageAnyChargerMassResetCommand,
  PermissionStoreModelEnum.ManageAnyGridTargetOverride,
  PermissionStoreModelEnum.ViewAlertSopLink,
  PermissionStoreModelEnum.ViewAnyPowerManagementDetails,
  PermissionStoreModelEnum.ViewAnyChargerUptime,
  PermissionStoreModelEnum.ViewAnyChargerLiveIssues,
  PermissionStoreModelEnum.ViewAnyPredictiveInfraOverview,
  PermissionStoreModelEnum.ViewAnyPredictiveInfraChargerPerformance,
  PermissionStoreModelEnum.ViewAnyPredictiveInfraVehicleHealth,
  PermissionStoreModelEnum.ManageAnyChargerMassCustomCommand,
  PermissionStoreModelEnum.ViewAnyEnergyTradingDashboard,
  PermissionStoreModelEnum.ViewAnyGbTradingPositionCalculation,
  PermissionStoreModelEnum.ManageAnyGbTradingPositionCalculation,
];

// eslint-disable-next-line unused-imports/no-unused-vars
const SystemViewPermissions = [
  PermissionStoreModelEnum.ViewAnySites,
  PermissionStoreModelEnum.ViewAnyDashboard,
  PermissionStoreModelEnum.ViewAnyAnalytics,
  PermissionStoreModelEnum.ViewAnyFinanceAnalytics,
  PermissionStoreModelEnum.ViewAnyIdTag,
  PermissionStoreModelEnum.ViewAnyVehicles,
  PermissionStoreModelEnum.ViewWorkflow,
  PermissionStoreModelEnum.ViewAnyAuditLogs,
  PermissionStoreModelEnum.ViewAnyKiosk,
  PermissionStoreModelEnum.ViewAnyReports,
  PermissionStoreModelEnum.ViewAnyPowerManagementDetails,
];

// eslint-disable-next-line unused-imports/no-unused-vars
const OperatorAdminPermissions = [
  PermissionStoreModelEnum.ViewOwnSites,
  PermissionStoreModelEnum.ViewOwnDashboard,
  PermissionStoreModelEnum.ManageOwnChargerBasicCommands,
  PermissionStoreModelEnum.ManageOwnChargerPowerCommand,
  PermissionStoreModelEnum.ManageOwnChargerResetCommand,
  PermissionStoreModelEnum.ManageOwnChargerReconnect,
  PermissionStoreModelEnum.ManageOwnChargerDiagnostics,
  PermissionStoreModelEnum.ManageOwnChargingSessionPriority,
  PermissionStoreModelEnum.ManageOwnBessCommands,
  PermissionStoreModelEnum.ViewOwnAnalytics,
  PermissionStoreModelEnum.ViewOwnFinanceAnalytics,
  PermissionStoreModelEnum.ViewOwnIdTag,
  PermissionStoreModelEnum.ManageOwnIdTag,
  PermissionStoreModelEnum.ManageOwnSchedule,
  PermissionStoreModelEnum.ViewOwnVehicles,
  PermissionStoreModelEnum.ViewOwnAuditLogs,
  PermissionStoreModelEnum.ViewOwnKiosk,
  PermissionStoreModelEnum.ManageOwnAlert,
  PermissionStoreModelEnum.ViewOwnReports,
  PermissionStoreModelEnum.ViewOwnPowerManagementDetails,
];

// eslint-disable-next-line unused-imports/no-unused-vars
const OperatorViewPermissions = [
  PermissionStoreModelEnum.ViewOwnSites,
  PermissionStoreModelEnum.ViewOwnDashboard,
  PermissionStoreModelEnum.ViewOwnAnalytics,
  PermissionStoreModelEnum.ViewOwnFinanceAnalytics,
  PermissionStoreModelEnum.ViewOwnIdTag,
  PermissionStoreModelEnum.ViewOwnVehicles,
  PermissionStoreModelEnum.ViewOwnAuditLogs,
  PermissionStoreModelEnum.ViewOwnKiosk,
  PermissionStoreModelEnum.ViewOwnReports,
  PermissionStoreModelEnum.ViewOwnSiteWithAiAssistant,
  PermissionStoreModelEnum.ViewOwnPowerManagementDetails,
];

export class MockKeycloakService {
  public readonly keycloakEvents$ = new Subject<KeycloakEvent>();
  public enableBearerInterceptor = false;
  public excludedUrls = [];
  public shouldAddToken = (): boolean => true;
  public shouldUpdateToken = (): boolean => true;
  public addTokenToHeader = (): Observable<HttpHeaders> => of({} as HttpHeaders);
  public clearToken = (): void => void 0;
  public getKeycloakInstance = (): Keycloak =>
    ({
      createLoginUrl: () => location.href,
      token: 'token',
      updateToken: () => Promise.resolve(false),
      refreshTokenParsed: {
        exp: (Date.now() + 1800 * 1000) / 1000,
      },
      timeSkew: -4,
      tokenParsed: {
        preferred_username: 'Mock User',
        sub: 'mock-user-id',
        organization: ['optimo'],
      },
    }) as unknown as Keycloak;
  public getToken = (): Promise<string> => Promise.resolve('token');
  public getUserRoles = (): string[] => SystemAdminPermissions;
  public getUsername = (): string => '';
  public init = (): Promise<boolean> => Promise.resolve(true);
  public isLoggedIn = (): Promise<boolean> => Promise.resolve(true);
  public isTokenExpired = (): boolean => false;
  public isUserInRole = (): boolean => true;
  public loadUserProfile = (): Promise<Keycloak.KeycloakProfile> => Promise.resolve({} as Keycloak.KeycloakProfile);
  public login = (): Promise<void> => Promise.resolve(void 0);
  public logout = (): Promise<void> => Promise.resolve(void 0);
  public register = (): Promise<void> => Promise.resolve(void 0);
  public updateToken = (): Promise<boolean> => Promise.resolve(false);
}
